import { UISimulation } from '@/components/help/UISimulation';

const STEP_DURATION = 2200;

function StepHeader({ step, label }: { step: number; label: string }): JSX.Element {
  return (
    <div className="flex items-center justify-between mb-3">
      <div className="text-[10px] font-mono uppercase tracking-wide text-dash-muted">
        Step {step} of 5
      </div>
      <div className="text-[11px] font-mono text-dash-secondary">{label}</div>
    </div>
  );
}

function DescribeFrame(): JSX.Element {
  return (
    <div className="rounded-md border border-dash-border bg-dash-bg p-3">
      <StepHeader step={1} label="Describe" />
      <label className="text-[11px] font-mono uppercase tracking-wide text-dash-muted">
        What is this site about?
      </label>
      <div className="mt-2 rounded border border-dash-border bg-dash-surface px-3 py-2 text-sm text-dash-primary min-h-[64px]">
        A site about home espresso for people switching from cafes.
      </div>
      <div className="mt-3 flex justify-end">
        <span className="rounded bg-accent/15 text-accent border border-accent/40 px-3 py-1 text-xs font-medium">
          Next
        </span>
      </div>
    </div>
  );
}

function ContextFrame(): JSX.Element {
  return (
    <div className="rounded-md border border-dash-border bg-dash-bg p-3">
      <StepHeader step={2} label="AI context" />
      <div className="space-y-2 text-xs">
        <div className="rounded border border-dash-border bg-dash-surface px-3 py-2">
          <div className="text-[10px] font-mono uppercase text-dash-muted">Audience</div>
          <div className="text-dash-primary">Cafe drinkers building a home setup on a budget.</div>
        </div>
        <div className="rounded border border-dash-border bg-dash-surface px-3 py-2">
          <div className="text-[10px] font-mono uppercase text-dash-muted">Voice</div>
          <div className="text-dash-primary">Friendly, hands-on, measurement-driven.</div>
        </div>
        <div className="rounded border border-dash-border bg-dash-surface px-3 py-2">
          <div className="text-[10px] font-mono uppercase text-dash-muted">Sensitivity</div>
          <div className="text-dash-primary">Low — consumer product reviews and how-tos.</div>
        </div>
      </div>
    </div>
  );
}

function PacksFrame(): JSX.Element {
  const packs = [
    { name: 'core', state: 'on', locked: true },
    { name: 'food', state: 'on', locked: false },
    { name: 'medical', state: 'off', locked: false },
    { name: 'finance', state: 'off', locked: false },
    { name: 'technology', state: 'off', locked: false },
  ];
  return (
    <div className="rounded-md border border-dash-border bg-dash-bg p-3">
      <StepHeader step={3} label="Component packs" />
      <ul className="space-y-1.5">
        {packs.map((p) => {
          const on = p.state === 'on';
          return (
            <li
              key={p.name}
              className="flex items-center justify-between rounded border border-dash-border bg-dash-surface px-3 py-1.5"
            >
              <span className="font-mono text-xs text-dash-primary">{p.name}</span>
              <span
                className={
                  on
                    ? 'text-[10px] font-mono uppercase tracking-wide text-success'
                    : 'text-[10px] font-mono uppercase tracking-wide text-dash-muted'
                }
              >
                {p.locked ? 'always on' : on ? 'enabled' : 'off'}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function PromptsFrame(): JSX.Element {
  return (
    <div className="rounded-md border border-dash-border bg-dash-bg p-3">
      <StepHeader step={4} label="Generating prompts" />
      <ul className="space-y-2 text-xs">
        {['Writer', 'Reviewer', 'Brainstorm', 'Illustrator'].map((p, i) => (
          <li
            key={p}
            className="flex items-center justify-between rounded border border-dash-border bg-dash-surface px-3 py-2"
          >
            <span className="text-dash-primary">{p}</span>
            <span
              className={
                i < 3
                  ? 'text-[10px] font-mono uppercase tracking-wide text-success'
                  : 'text-[10px] font-mono uppercase tracking-wide text-warning'
              }
            >
              {i < 3 ? 'ready' : 'generating...'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CreatedFrame(): JSX.Element {
  return (
    <div className="rounded-md border border-success/40 bg-success/10 p-4 text-center">
      <div className="text-[10px] font-mono uppercase tracking-wide text-success mb-2">
        Step 5 of 5 — Site created
      </div>
      <div className="text-sm font-semibold text-dash-primary">
        home-espresso.pages.dev
      </div>
      <div className="text-xs text-dash-secondary mt-1">
        Cloudflare project provisioned. First deploy queued.
      </div>
      <div className="mt-3 inline-flex items-center gap-2 rounded border border-dash-border bg-dash-bg px-3 py-1 text-xs text-dash-primary">
        Open site hub
      </div>
    </div>
  );
}

export function WizardSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <DescribeFrame />, label: 'Describe the site', duration: STEP_DURATION },
        { frame: <ContextFrame />, label: 'AI generates site context', duration: STEP_DURATION },
        { frame: <PacksFrame />, label: 'Pick component packs', duration: STEP_DURATION },
        { frame: <PromptsFrame />, label: 'Prompts generated', duration: STEP_DURATION },
        { frame: <CreatedFrame />, label: 'Site created', duration: STEP_DURATION },
      ]}
    />
  );
}
